import { ref } from 'vue'
import { defineStore } from 'pinia'
import type { NotificationCategory, NotificationPreferenceDto } from '@roadfootage/api-client'
import { useApi } from '../../composables/api'

export type SetEmailEnabledResult = { ok: true } | { ok: false; message: string }

const SAVE_ERROR = 'Could not save your notification setting. Please try again.'

export const useNotificationPreferencesStore = defineStore('notificationPreferences', () => {
  const { meApi } = useApi()

  const preferences = ref<NotificationPreferenceDto[]>([])
  const loading = ref(false)
  const loadFailed = ref(false)

  async function load() {
    loading.value = true
    loadFailed.value = false
    try {
      const response = await meApi.meGetNotificationPreferences()
      preferences.value = response.preferences ?? []
    } catch (error) {
      console.error('Failed to load notification preferences', error)
      loadFailed.value = true
    } finally {
      loading.value = false
    }
  }

  // Optimistic: the toggle flips at once, only the changed category is sent, and a failure puts it back.
  async function setEmailEnabled(
    category: NotificationCategory,
    emailEnabled: boolean
  ): Promise<SetEmailEnabledResult> {
    const row = preferences.value.find((p) => p.category === category)
    if (!row) return { ok: false, message: 'Unknown notification category.' }
    if (row.isTransactional) return { ok: false, message: 'This notification is always sent.' }

    const previous = row.emailEnabled
    row.emailEnabled = emailEnabled
    try {
      const response = await meApi.meSetNotificationPreferences({
        updateNotificationPreferencesRequest: { preferences: [{ category, emailEnabled }] }
      })
      preferences.value = response.preferences ?? preferences.value
      return { ok: true }
    } catch (error) {
      console.error('Failed to save notification preference', error)
      // Re-find the row: a load may have replaced the list while the PUT was in flight.
      const current = preferences.value.find((p) => p.category === category)
      if (current) current.emailEnabled = previous
      return { ok: false, message: SAVE_ERROR }
    }
  }

  return { preferences, loading, loadFailed, load, setEmailEnabled }
})
